import { z } from 'zod';
import apiClient from '@shared/api/http';
import { toItemArray, toSnakeCaseKeys } from '@shared/api/keys';
import type { DeviceInstanceId, VmId } from '../domain/ids';

// T39+T40 (fusionadas, ver tasks.md): v1 unifica 3 recursos legado independientes
// (`/vms/{vmId}/subsystems`, `/vm-sap-instances`, `/database-instances`) en UNO solo,
// `/device-instances/{deviceInstanceId}/virtual-machines/{vmId}/software-associations`,
// discriminado por `category` (`subsystem`|`database_instance`|`sap_instance`). El DTO de v1 ya no
// tiene campos tipados propios por categoría: solo `id` (fila de asociación), `virtualMachineId`,
// `category`, `catalogSelectionId` (el id del subsistema/producto SAP/sistema de BD en el
// catálogo), `attributes` (bolsa libre `Record<string, unknown>` con el resto de campos
// específicos de cada categoría) y `context.catalogSelectionLabel` (única enriquecimiento de
// catálogo que sobrevive — un string, no el objeto completo que el legado sí traía).
//
// `deviceInstanceId` es OBLIGATORIO en el path de los 5 endpoints (list/create/update/delete/
// version-change) — plan.md §7 documentaba esto solo para `vmSubsystemRepository` (C-5), pero el
// contrato real exige lo mismo para `sapInstanceRepository`/`databaseInstanceRepository`: de ahí
// que T39 y T40 se implementaran juntas sobre este repositorio de bajo nivel compartido.
//
// `UpdateSoftwareAssociationRequestDto` exige `version` (concurrencia optimista) — no hay un GET de
// una sola asociación en v1 (solo el listado), así que cada repositorio de dominio relee `getAll`
// filtrado y busca por `id` antes de hacer PATCH (mismo principio de "releer antes de actualizar"
// que T31/T38, adaptado a la ausencia de endpoint de detalle).
//
// Ningún verbo de este recurso acepta `change_request_number` (create/update/delete/version-change)
// — mismo hueco no bloqueante ya visto en T36/T37/T38 (vm/vmDocument/vmCluster `remove`): el gate de
// change request (`useChangeRequestGate`) se sigue exigiendo en la UI, pero el número ya no viaja al
// backend para esta familia de endpoints.
export type SoftwareAssociationCategory = 'subsystem' | 'database_instance' | 'sap_instance';

const SoftwareAssociationDtoSchema = z.object({
  id: z.string(),
  virtual_machine_id: z.string(),
  category: z.enum(['subsystem', 'database_instance', 'sap_instance']),
  catalog_selection_id: z.string(),
  attributes: z.record(z.string(), z.unknown()),
  context: z
    .object({ catalog_selection_label: z.string().optional() })
    .partial()
    .nullish(),
  version: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type SoftwareAssociationDto = z.infer<typeof SoftwareAssociationDtoSchema>;

export interface CreateSoftwareAssociationPayload {
  category: SoftwareAssociationCategory;
  catalogSelectionId: string;
  attributes: Record<string, unknown>;
}

export interface UpdateSoftwareAssociationPayload {
  version: number;
  attributes: Record<string, unknown>;
}

export interface ChangeAssociationVersionPayload {
  catalogSelectionId: string;
}

function basePath(deviceInstanceId: DeviceInstanceId, vmId: VmId): string {
  return `/device-instances/${deviceInstanceId}/virtual-machines/${vmId}/software-associations`;
}

function parseOne(data: unknown): SoftwareAssociationDto {
  return SoftwareAssociationDtoSchema.parse(toSnakeCaseKeys(data));
}

export const softwareAssociationRepository = {
  async getAll(
    deviceInstanceId: DeviceInstanceId,
    vmId: VmId,
    category: SoftwareAssociationCategory
  ): Promise<SoftwareAssociationDto[]> {
    const { data } = await apiClient.get(basePath(deviceInstanceId, vmId));
    return z
      .array(SoftwareAssociationDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .filter((association) => association.category === category);
  },

  async create(
    deviceInstanceId: DeviceInstanceId,
    vmId: VmId,
    payload: CreateSoftwareAssociationPayload
  ): Promise<SoftwareAssociationDto> {
    const { data } = await apiClient.post(basePath(deviceInstanceId, vmId), payload);
    return parseOne(data);
  },

  async update(
    deviceInstanceId: DeviceInstanceId,
    vmId: VmId,
    associationId: string,
    payload: UpdateSoftwareAssociationPayload
  ): Promise<SoftwareAssociationDto> {
    const { data } = await apiClient.patch(
      `${basePath(deviceInstanceId, vmId)}/${associationId}`,
      payload
    );
    return parseOne(data);
  },

  async remove(deviceInstanceId: DeviceInstanceId, vmId: VmId, associationId: string): Promise<void> {
    await apiClient.delete(`${basePath(deviceInstanceId, vmId)}/${associationId}`);
  },

  async changeVersion(
    deviceInstanceId: DeviceInstanceId,
    vmId: VmId,
    associationId: string,
    payload: ChangeAssociationVersionPayload
  ): Promise<SoftwareAssociationDto> {
    const { data } = await apiClient.post(
      `${basePath(deviceInstanceId, vmId)}/${associationId}/version-change`,
      payload
    );
    return parseOne(data);
  },
};
